import { Component, OnInit, ChangeDetectorRef, ChangeDetectionStrategy, DestroyRef, inject , signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SpeechService, VoiceCommand } from 'src/app/services/media/speech.service';
import { AccountService, SubscriptionType } from 'src/app/services/account/account.service';
import { SettingsService } from 'src/app/services/account/settings.service';
import { skip } from 'rxjs/operators';
import { ConsoleLoggerService } from 'src/app/services/logging/console-logger.service';
import { VoiceCommandErrorDialogComponent } from '../modals/voice-command-error-dialog/voice-command-error-dialog.component';
import device from 'current-device';
import { WebviewService } from 'src/app/services/platform/webview.service';
import { SubscriptionRequiredDialogComponent } from '../modals/subscriptions-dialog/subscription-required-dialog/subscription-required-dialog.component';
import { CommonModule } from '@angular/common';
import { StackedModalsService } from 'src/app/services/modal/stacked-modals.service';
import { ModalRef } from 'src/app/services/modal';

@Component({
  standalone: true,
  selector: 'app-voice-input',
  templateUrl: './voice-input.component.html',
  styleUrls: ['./voice-input.component.scss'],
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class VoiceInputComponent implements OnInit {
  public readonly IsActive = signal<boolean>(false);

  private readonly destroyRef = inject(DestroyRef);
  private errorDialogRef: ModalRef<VoiceCommandErrorDialogComponent> | null = null;

  constructor(
    public SpeechRecognitionService: SpeechService,
    private cdr: ChangeDetectorRef,
    public accountService: AccountService,
    public settingsService: SettingsService,
    private consoleLoggerService: ConsoleLoggerService,
    public webviewService: WebviewService,
    private stackedModalsService: StackedModalsService) { }

  ngOnInit(): void {
    this.SpeechRecognitionService.ObservableActiveVoiceCommands.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((voiceCommands: VoiceCommand[] | null) => {
      if (voiceCommands != null) {
        this.IsActive.set(voiceCommands.indexOf(VoiceCommand.SetBattery) != -1);
      }
    });
  }

  public ToggleSpeechRecognition(): void {
    if (this.accountService.getUserSubscriptionType() == SubscriptionType.Free) {
      this.stackedModalsService.openModal(SubscriptionRequiredDialogComponent, { data: { feature: "voice-input" }, autoFocus: false });
    }
    else {
      const desktopEdge = device.desktop() && window.navigator.userAgent.indexOf("Edg") > -1;
      if (('SpeechRecognition' in window || 'webkitSpeechRecognition' in window) && !desktopEdge) {
        if (this.IsActive()) {
          this.SpeechRecognitionService.StopRecognition();
        } else {
          const iosNativeApp = this.webviewService.IsGPSTWebview() && device.ios();
          if (iosNativeApp) {
            this.webviewService.startMicrophoneInteraction();
          }
          else {
            this.StartRecognition();
          }
        }
      }
      else {
        this.openVoiceInputErrorDialog();
      }
    }
  }

  private openVoiceInputErrorDialog(): void {
    this.errorDialogRef = this.stackedModalsService.openModal(VoiceCommandErrorDialogComponent,
      {
        data: {
          errorMsg: "This feature is not supported on this device.",
          notSupported: true
        },
        autoFocus: false
      });

    this.errorDialogRef.afterClosed().subscribe(() => {
      this.errorDialogRef = null;
    });
  }

  private StartRecognition(): void {
    this.SpeechRecognitionService.StartRecognition(
      [VoiceCommand.SetBattery, VoiceCommand.SkipWP, VoiceCommand.SkipCS,
      VoiceCommand.StopNavigation, VoiceCommand.SetWeight, VoiceCommand.NavigateToHome,
      VoiceCommand.NavigateToWork, VoiceCommand.ReadEta, VoiceCommand.ReadEte, VoiceCommand.ReadRemainingDist]
    );
  }
}
